/**
 * Watchlists (owner 2026-09-28: "a watch list feature like TradingView has that can pop out on the
 * right hand side"; DESIGN-REFERENCE "TradingView right sidebar"). The pure model behind the
 * right-sidebar Watchlist tab and the Watchlist module, which show the same lists.
 *
 * One synced document per user, `watchlist/main` (field-merge type, SPEC §1A.3): several named
 * lists, each an ordered mix of symbols and section headers (TradingView "sections"); the active
 * list; per-list column sort; and colour flags per symbol (TradingView flagged symbols: one colour
 * per symbol, user-level, shown in every list). Everything here is pure so list CRUD, reordering,
 * sections, sorting (within sections, as TradingView), search matching and the migration from the
 * old fixed module list are unit-tested without a browser.
 */

export const WATCHLIST_DOC_VERSION = 1;
export const WATCHLIST_DOC_ID = "main";
export const MAX_LISTS = 50;
/** TradingView's limit per list (help "How many symbols can I add"). */
export const MAX_ITEMS = 1000;
export const MAX_LIST_NAME = 40;
export const MAX_SECTION_TITLE = 40;
export const MAX_SYMBOL_LEN = 24;

/** Flag colours (TradingView flagged-symbol colours; one per symbol). */
export const WATCH_FLAGS = ["red", "orange", "yellow", "green", "cyan", "blue", "purple"] as const;
export type WatchFlag = (typeof WATCH_FLAGS)[number];

export type WatchSymbolItem = { type: "symbol"; symbol: string };
export type WatchSectionItem = { type: "section"; id: string; title: string; collapsed: boolean };
export type WatchItem = WatchSymbolItem | WatchSectionItem;

export type WatchColumn = "symbol" | "last" | "chg" | "chgPct" | "vol";
export type WatchSort = { key: WatchColumn; dir: 1 | -1 };

export type Watchlist = {
  id: string;
  name: string;
  items: WatchItem[];
  /** Column sort (null = the user's own order). Sorting never rewrites `items`. */
  sort: WatchSort | null;
};

export type WatchlistDoc = {
  v: typeof WATCHLIST_DOC_VERSION;
  activeId: string;
  lists: Watchlist[];
  /** Colour flag per symbol (upper-case), user-level. */
  flags: Record<string, WatchFlag>;
};

// ---------------------------------------------------------------------------------------------
// Names and symbols

function clean(s: string, max: number): string {
  return s.replace(/\s+/g, " ").trim().slice(0, max).trim();
}

export function cleanListName(name: string): string {
  return clean(name, MAX_LIST_NAME);
}

/** Upper-case, no spaces; "" when not a plausible symbol (letters, digits, `.`, `_`, `-`, `/`). */
export function cleanSymbol(symbol: string): string {
  const s = symbol.replace(/\s+/g, "").toUpperCase();
  return s.length > 0 && s.length <= MAX_SYMBOL_LEN && /^[A-Z0-9][A-Z0-9._\-/]*$/.test(s) ? s : "";
}

/** `base` if unused, else `base 2`, `base 3` … (case-insensitive). */
export function uniqueListName(doc: WatchlistDoc | null, base: string): string {
  const taken = new Set((doc?.lists ?? []).map((l) => l.name.toLowerCase()));
  const b = cleanListName(base) || "Watchlist";
  if (!taken.has(b.toLowerCase())) return b;
  for (let n = 2; ; n++) {
    const c = `${b.slice(0, MAX_LIST_NAME - String(n).length - 1)} ${String(n)}`;
    if (!taken.has(c.toLowerCase())) return c;
  }
}

export const itemKey = (it: WatchItem): string => (it.type === "symbol" ? `s:${it.symbol}` : `h:${it.id}`);

// ---------------------------------------------------------------------------------------------
// Defaults and migration

/**
 * The list the Watchlist module showed before lists were synced (a fixed "Futures" group, owner
 * mockup 01): new users start with exactly that, so nothing moves under existing users.
 */
export const LEGACY_WATCHLIST_GROUPS: ReadonlyArray<{ name: string; symbols: readonly string[] }> = [
  { name: "Futures", symbols: ["NQ", "ES", "MNQ", "MES", "RTY", "CL", "GC", "ZB"] },
];

export function defaultWatchlistDoc(): WatchlistDoc {
  const items: WatchItem[] = [];
  LEGACY_WATCHLIST_GROUPS.forEach((g, i) => {
    items.push({ type: "section", id: `sec-${String(i + 1)}`, title: g.name, collapsed: false });
    for (const s of g.symbols) items.push({ type: "symbol", symbol: s });
  });
  return {
    v: WATCHLIST_DOC_VERSION,
    activeId: "main",
    lists: [{ id: "main", name: "Main", items, sort: null }],
    flags: {},
  };
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

const isFlag = (v: unknown): v is WatchFlag => (WATCH_FLAGS as readonly unknown[]).includes(v);
const COLUMNS: readonly WatchColumn[] = ["symbol", "last", "chg", "chgPct", "vol"];

function sanitizeItems(raw: unknown): WatchItem[] {
  if (!Array.isArray(raw)) return [];
  const out: WatchItem[] = [];
  const syms = new Set<string>();
  const secs = new Set<string>();
  for (const it of raw as unknown[]) {
    if (out.length >= MAX_ITEMS) break;
    // A bare string is a symbol (older / hand-written documents).
    if (typeof it === "string") {
      const s = cleanSymbol(it);
      if (s && !syms.has(s)) {
        syms.add(s);
        out.push({ type: "symbol", symbol: s });
      }
      continue;
    }
    if (!isRecord(it)) continue;
    if (it["type"] === "section" && typeof it["id"] === "string" && it["id"] && !secs.has(it["id"])) {
      secs.add(it["id"]);
      const title = typeof it["title"] === "string" ? clean(it["title"], MAX_SECTION_TITLE) : "";
      out.push({
        type: "section",
        id: it["id"],
        title: title || "Section",
        collapsed: it["collapsed"] === true,
      });
    } else if (it["type"] === "symbol" && typeof it["symbol"] === "string") {
      const s = cleanSymbol(it["symbol"]);
      if (s && !syms.has(s)) {
        syms.add(s);
        out.push({ type: "symbol", symbol: s });
      }
    }
  }
  return out;
}

function sanitizeSort(raw: unknown): WatchSort | null {
  if (!isRecord(raw)) return null;
  const key = raw["key"];
  const dir = raw["dir"];
  return COLUMNS.includes(key as WatchColumn) && (dir === 1 || dir === -1)
    ? { key: key as WatchColumn, dir }
    : null;
}

/**
 * Validate a stored or synced document. Bad lists / items are dropped instead of failing the
 * whole document; list ids are de-duplicated, names repaired, the active id fixed. Also accepts:
 * - `{ symbols: string[] }` or a bare `string[]` (a single list, e.g. hand-written), and
 * - `{ groups: [{ name, symbols }] }` (the old module's fixed groups) → sections of one list.
 * Null when nothing usable is left.
 */
export function sanitizeWatchlistDoc(raw: unknown): WatchlistDoc | null {
  if (Array.isArray(raw)) return sanitizeWatchlistDoc({ symbols: raw });
  if (!isRecord(raw)) return null;
  if (!Array.isArray(raw["lists"])) {
    const legacy = migrateLegacy(raw);
    return legacy ? sanitizeWatchlistDoc(legacy) : null;
  }
  const lists: Watchlist[] = [];
  const ids = new Set<string>();
  for (const l of raw["lists"] as unknown[]) {
    if (lists.length >= MAX_LISTS) break;
    if (!isRecord(l) || typeof l["id"] !== "string" || !l["id"] || ids.has(l["id"])) continue;
    ids.add(l["id"]);
    const name = typeof l["name"] === "string" ? cleanListName(l["name"]) : "";
    lists.push({
      id: l["id"],
      name: name || `Watchlist ${String(lists.length + 1)}`,
      items: sanitizeItems(l["items"]),
      sort: sanitizeSort(l["sort"]),
    });
  }
  const first = lists[0];
  if (!first) return null;
  const activeId =
    typeof raw["activeId"] === "string" && ids.has(raw["activeId"]) ? raw["activeId"] : first.id;
  const flags: Record<string, WatchFlag> = {};
  if (isRecord(raw["flags"])) {
    for (const [k, v] of Object.entries(raw["flags"])) {
      const s = cleanSymbol(k);
      if (s && isFlag(v)) flags[s] = v;
    }
  }
  return { v: WATCHLIST_DOC_VERSION, activeId, lists, flags };
}

function migrateLegacy(raw: Record<string, unknown>): Record<string, unknown> | null {
  if (Array.isArray(raw["symbols"])) {
    return { activeId: "main", lists: [{ id: "main", name: "Main", items: raw["symbols"] }] };
  }
  if (Array.isArray(raw["groups"])) {
    const items: unknown[] = [];
    (raw["groups"] as unknown[]).forEach((g, i) => {
      if (!isRecord(g) || !Array.isArray(g["symbols"])) return;
      items.push({
        type: "section",
        id: `sec-${String(i + 1)}`,
        title: typeof g["name"] === "string" ? g["name"] : "Section",
      });
      items.push(...(g["symbols"] as unknown[]));
    });
    return { activeId: "main", lists: [{ id: "main", name: "Main", items }] };
  }
  return null;
}

// ---------------------------------------------------------------------------------------------
// Lists

export function activeList(doc: WatchlistDoc): Watchlist {
  const l = doc.lists.find((x) => x.id === doc.activeId) ?? doc.lists[0];
  if (!l) throw new Error("watchlist document has no list");
  return l;
}

function mapList(doc: WatchlistDoc, id: string, f: (l: Watchlist) => Watchlist): WatchlistDoc {
  const i = doc.lists.findIndex((l) => l.id === id);
  const cur = doc.lists[i];
  if (!cur) return doc;
  const next = f(cur);
  if (next === cur) return doc;
  const lists = [...doc.lists];
  lists[i] = next;
  return { ...doc, lists };
}

/** New empty list (unique name), made active. No-op at the cap or on an id clash. */
export function createList(doc: WatchlistDoc, id: string, name?: string): WatchlistDoc {
  if (doc.lists.length >= MAX_LISTS || doc.lists.some((l) => l.id === id)) return doc;
  const list: Watchlist = { id, name: uniqueListName(doc, name ?? "Watchlist"), items: [], sort: null };
  return { ...doc, activeId: id, lists: [...doc.lists, list] };
}

/** Copy of a list ("<name> copy") right after it, made active (TradingView "Make a copy"). */
export function copyList(doc: WatchlistDoc, srcId: string, id: string): WatchlistDoc {
  const src = doc.lists.find((l) => l.id === srcId);
  if (!src || doc.lists.length >= MAX_LISTS || doc.lists.some((l) => l.id === id)) return doc;
  const at = doc.lists.indexOf(src);
  const copy: Watchlist = {
    id,
    name: uniqueListName(doc, `${src.name.slice(0, MAX_LIST_NAME - 5)} copy`),
    items: src.items.map((it) => ({ ...it })),
    sort: src.sort,
  };
  const lists = [...doc.lists];
  lists.splice(at + 1, 0, copy);
  return { ...doc, activeId: id, lists };
}

/** Rename; blank names and names another list already has are ignored. */
export function renameList(doc: WatchlistDoc, id: string, name: string): WatchlistDoc {
  const n = cleanListName(name);
  if (!n) return doc;
  if (doc.lists.some((l) => l.id !== id && l.name.toLowerCase() === n.toLowerCase())) return doc;
  return mapList(doc, id, (l) => (l.name === n ? l : { ...l, name: n }));
}

/** Delete. The last list cannot be deleted; deleting the active one activates its neighbour. */
export function deleteList(doc: WatchlistDoc, id: string): WatchlistDoc {
  const i = doc.lists.findIndex((l) => l.id === id);
  if (i < 0 || doc.lists.length <= 1) return doc;
  const lists = doc.lists.filter((l) => l.id !== id);
  const activeId = doc.activeId === id ? ((lists[i] ?? lists[i - 1])?.id ?? doc.activeId) : doc.activeId;
  return { ...doc, activeId, lists };
}

export function switchList(doc: WatchlistDoc, id: string): WatchlistDoc {
  return doc.activeId === id || !doc.lists.some((l) => l.id === id) ? doc : { ...doc, activeId: id };
}

/** Empty a list (TradingView "Clear the list"). */
export function clearList(doc: WatchlistDoc, id: string): WatchlistDoc {
  return mapList(doc, id, (l) => (l.items.length === 0 ? l : { ...l, items: [] }));
}

// ---------------------------------------------------------------------------------------------
// Items

/**
 * Add a symbol. It goes after `afterKey` (an item key: a symbol row, or a section header = top of
 * that section), else at the end (TradingView: new symbols go to the bottom; right-click "Add
 * symbol" inserts at that point). A symbol already in the list is not added twice.
 */
export function addSymbol(
  doc: WatchlistDoc,
  listId: string,
  symbol: string,
  afterKey?: string,
): WatchlistDoc {
  const s = cleanSymbol(symbol);
  if (!s) return doc;
  return mapList(doc, listId, (l) => {
    if (l.items.length >= MAX_ITEMS || l.items.some((it) => it.type === "symbol" && it.symbol === s))
      return l;
    const items = [...l.items];
    const at = afterKey === undefined ? -1 : items.findIndex((it) => itemKey(it) === afterKey);
    items.splice(at < 0 ? items.length : at + 1, 0, { type: "symbol", symbol: s });
    return { ...l, items };
  });
}

export function hasSymbol(list: Watchlist, symbol: string): boolean {
  const s = cleanSymbol(symbol);
  return list.items.some((it) => it.type === "symbol" && it.symbol === s);
}

/** Remove an item (symbol row or section header; a section's symbols stay, joining the one above). */
export function removeItem(doc: WatchlistDoc, listId: string, key: string): WatchlistDoc {
  return mapList(doc, listId, (l) => {
    const items = l.items.filter((it) => itemKey(it) !== key);
    return items.length === l.items.length ? l : { ...l, items };
  });
}

/** Add a section header after `afterKey` (else at the end). */
export function addSection(
  doc: WatchlistDoc,
  listId: string,
  id: string,
  title: string,
  afterKey?: string,
): WatchlistDoc {
  const t = clean(title, MAX_SECTION_TITLE) || "Section";
  return mapList(doc, listId, (l) => {
    if (l.items.length >= MAX_ITEMS || l.items.some((it) => it.type === "section" && it.id === id)) return l;
    const items = [...l.items];
    const at = afterKey === undefined ? -1 : items.findIndex((it) => itemKey(it) === afterKey);
    items.splice(at < 0 ? items.length : at + 1, 0, { type: "section", id, title: t, collapsed: false });
    return { ...l, items };
  });
}

export function renameSection(doc: WatchlistDoc, listId: string, id: string, title: string): WatchlistDoc {
  const t = clean(title, MAX_SECTION_TITLE);
  if (!t) return doc;
  return mapList(doc, listId, (l) => ({
    ...l,
    items: l.items.map((it) => (it.type === "section" && it.id === id ? { ...it, title: t } : it)),
  }));
}

export function toggleSection(doc: WatchlistDoc, listId: string, id: string): WatchlistDoc {
  return mapList(doc, listId, (l) => ({
    ...l,
    items: l.items.map((it) =>
      it.type === "section" && it.id === id ? { ...it, collapsed: !it.collapsed } : it,
    ),
  }));
}

/**
 * Move an item to `toIndex` in the list's own (unsorted) order, clamped. Moving a symbol into a
 * sorted list keeps the column sort (the view re-sorts); the stored order changes underneath.
 */
export function moveItem(doc: WatchlistDoc, listId: string, key: string, toIndex: number): WatchlistDoc {
  return mapList(doc, listId, (l) => {
    const i = l.items.findIndex((it) => itemKey(it) === key);
    if (i < 0) return l;
    const items = [...l.items];
    const [it] = items.splice(i, 1);
    if (!it) return l;
    const to = Math.max(0, Math.min(items.length, Math.trunc(toIndex)));
    if (to === i) return l;
    items.splice(to, 0, it);
    return { ...l, items };
  });
}

/**
 * Drag-and-drop: drop `key` before the item `beforeKey` (null = at the end) and clear the column
 * sort. On a sorted list the view first freezes what it shows with `applySortToOrder`, so the rows
 * stay where the user sees them (TradingView: moving a symbol saves the current sort order).
 */
export function dropItem(
  doc: WatchlistDoc,
  listId: string,
  key: string,
  beforeKey: string | null,
): WatchlistDoc {
  const l = doc.lists.find((x) => x.id === listId);
  if (!l || key === beforeKey) return doc;
  const from = l.items.findIndex((it) => itemKey(it) === key);
  if (from < 0) return doc;
  const rest = l.items.filter((it) => itemKey(it) !== key);
  const at = beforeKey === null ? rest.length : rest.findIndex((it) => itemKey(it) === beforeKey);
  if (at < 0) return doc;
  const moved = l.items[from];
  if (!moved) return doc;
  rest.splice(at, 0, moved);
  const same = rest.every((it, i) => it === l.items[i]);
  if (same && l.sort === null) return doc;
  return mapList(doc, listId, (x) => ({ ...x, items: rest, sort: null }));
}

/**
 * Freeze the current column sort into the stored order and clear the sort (before a drag, so the
 * rows stay where the user sees them).
 */
export function applySortToOrder(
  doc: WatchlistDoc,
  listId: string,
  valueOf: (symbol: string, key: WatchColumn) => number | string | null,
): WatchlistDoc {
  const l = doc.lists.find((x) => x.id === listId);
  if (!l || l.sort === null) return doc;
  const items = sortedItems(l.items, l.sort, valueOf);
  return mapList(doc, listId, (x) => ({ ...x, items, sort: null }));
}

// ---------------------------------------------------------------------------------------------
// Sort and flags

/** Header click cycle: ascending (symbol) / descending (numbers) → the other direction → off. */
export function nextSort(cur: WatchSort | null, key: WatchColumn): WatchSort | null {
  const first: 1 | -1 = key === "symbol" ? 1 : -1;
  if (cur === null || cur.key !== key) return { key, dir: first };
  if (cur.dir === first) return { key, dir: first === 1 ? -1 : 1 };
  return null;
}

export function setSort(doc: WatchlistDoc, listId: string, sort: WatchSort | null): WatchlistDoc {
  return mapList(doc, listId, (l) =>
    (l.sort === null && sort === null) ||
    (l.sort && sort && l.sort.key === sort.key && l.sort.dir === sort.dir)
      ? l
      : { ...l, sort },
  );
}

/**
 * Items in display order: each section's symbols (and the symbols before the first section) are
 * sorted by `sort`; sections keep their place (TradingView sorts within sections). Unknown values
 * sort last in either direction; ties keep the user's order.
 */
export function sortedItems(
  items: readonly WatchItem[],
  sort: WatchSort | null,
  valueOf: (symbol: string, key: WatchColumn) => number | string | null,
): WatchItem[] {
  if (sort === null) return [...items];
  const out: WatchItem[] = [];
  let run: WatchSymbolItem[] = [];
  const flush = (): void => {
    const keyed = run.map((it, i) => ({ it, i, v: valueOf(it.symbol, sort.key) }));
    keyed.sort((a, b) => {
      if (a.v === null && b.v !== null) return 1;
      if (b.v === null && a.v !== null) return -1;
      if (a.v !== null && b.v !== null && a.v !== b.v) {
        const c =
          typeof a.v === "string" || typeof b.v === "string"
            ? String(a.v).localeCompare(String(b.v))
            : a.v - b.v;
        if (c !== 0) return c * sort.dir;
      }
      return a.i - b.i;
    });
    for (const k of keyed) out.push(k.it);
    run = [];
  };
  for (const it of items) {
    if (it.type === "section") {
      flush();
      out.push(it);
    } else run.push(it);
  }
  flush();
  return out;
}

/** Rows to draw: display order minus the symbols inside collapsed sections. */
export function visibleItems(items: readonly WatchItem[]): WatchItem[] {
  const out: WatchItem[] = [];
  let hidden = false;
  for (const it of items) {
    if (it.type === "section") {
      hidden = it.collapsed;
      out.push(it);
    } else if (!hidden) out.push(it);
  }
  return out;
}

export function setFlag(doc: WatchlistDoc, symbol: string, flag: WatchFlag | null): WatchlistDoc {
  const s = cleanSymbol(symbol);
  if (!s || (doc.flags[s] ?? null) === flag) return doc;
  const flags = Object.fromEntries(Object.entries(doc.flags).filter(([k]) => k !== s));
  if (flag !== null) flags[s] = flag;
  return { ...doc, flags };
}

/** Next flag in the row's flag button cycle: none → red → … → purple → none. */
export function nextFlag(cur: WatchFlag | undefined): WatchFlag | null {
  if (cur === undefined) return WATCH_FLAGS[0];
  const i = WATCH_FLAGS.indexOf(cur);
  return WATCH_FLAGS[i + 1] ?? null;
}

/** Keyboard ↑/↓ over the visible symbol rows: the next symbol, or null at either end. */
export function stepSymbol(
  items: readonly WatchItem[],
  current: string | null,
  delta: 1 | -1,
): string | null {
  const syms = visibleItems(items).flatMap((it) => (it.type === "symbol" ? [it.symbol] : []));
  if (syms.length === 0) return null;
  const i = current === null ? -1 : syms.indexOf(current);
  if (i < 0) return delta === 1 ? (syms[0] ?? null) : (syms.at(-1) ?? null);
  return syms[i + delta] ?? null;
}
